import type {
  OperationsSnapshot,
  RunAggregateProjectionV2,
  WebTestRun
} from '@blume/web-test-model'
import { LoaderCircle, Menu, ShieldCheck, TriangleAlert, X } from 'lucide-react'
import { useEffect, useReducer, useRef, useState } from 'react'

import { buildDashboardSignInUrl, createDashboardApi, DashboardApiError } from '@/api'
import { CandidateReviewPage } from '@/app/candidate-review-page'
import { OperationsPage } from '@/app/operations-page'
import { BackLink } from '@/app/parts'
import { PullRequestDetailPage } from '@/app/pr-detail-page'
import { PullRequestsPage } from '@/app/pull-requests-page'
import { RunDetailPage } from '@/app/run-detail-page'
import { RunsPage } from '@/app/runs-page'
import { ScenarioDetailPage } from '@/app/scenario-detail-page'
import { ScenariosPage } from '@/app/scenarios-page'
import { SuiteDetailPage } from '@/app/suite-detail-page'
import { SuitesPage } from '@/app/suites-page'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { blumeFlowerUrl } from '@/design/brand'
import { ThemeToggle } from '@/design/theme'
import { cn } from '@/lib/utils'
import { Badge } from '@/ui/atoms/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/atoms/tooltip'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/ui/molecules/sheet'
import { useCandidateReview } from '@/live/use-candidate'
import { useRunFeed } from '@/live/use-run-feed'
import {
  mergeRunListsWithoutRegression,
  parseDashboardRoute,
  preservePaginationProgress,
  type Section
} from '@/logic'
import { fixtureOperations, fixtureRuns } from '@/data'

const sections: Array<{ id: Section; label: string }> = [
  { id: 'runs', label: 'Runs' },
  { id: 'pull-requests', label: 'Pull requests' },
  { id: 'suites', label: 'Suites' },
  { id: 'scenarios', label: 'Scenarios' },
  { id: 'operations', label: 'Operations' }
]

const workspaceRefreshIntervalMs = 3_000
const workspaceRefreshTimeoutMs = 10_000

type ConnectionState = 'fixture' | 'loading' | 'live' | 'degraded' | 'error'
type DashboardAccessState = 'signed-out' | 'non-admin' | 'unavailable'
type AggregateKind = Extract<
  RunAggregateProjectionV2['kind'],
  'pull-request' | 'suite' | 'scenario'
>
type WorkspaceAggregates = Partial<Record<AggregateKind, RunAggregateProjectionV2[]>>

interface DashboardDataState {
  runs: WebTestRun[]
  aggregates: WorkspaceAggregates
  operations?: OperationsSnapshot
  connectionState: ConnectionState
  loadError: string
  nextOffset?: number
}

type DashboardDataAction =
  | {
      type: 'refresh-success'
      runs: WebTestRun[]
      pagination: { limit: number }
      pullRequests: RunAggregateProjectionV2[]
      suites: RunAggregateProjectionV2[]
      scenarios: RunAggregateProjectionV2[]
      operations: OperationsSnapshot
    }
  | { type: 'unauthorized' }
  | { type: 'refresh-error'; message: string }
  | {
      type: 'load-more-success'
      runs: WebTestRun[]
      pagination: { limit: number; offset: number }
    }
  | { type: 'load-more-error'; message: string }
  | { type: 'connection'; value: ConnectionState }

function dashboardDataReducer(
  state: DashboardDataState,
  action: DashboardDataAction
): DashboardDataState {
  switch (action.type) {
    case 'refresh-success':
      return {
        runs: mergeRunListsWithoutRegression(state.runs, action.runs),
        aggregates: {
          'pull-request': action.pullRequests,
          suite: action.suites,
          scenario: action.scenarios
        },
        operations: action.operations,
        nextOffset: preservePaginationProgress(state.nextOffset, {
          runs: action.runs,
          pagination: action.pagination
        }),
        connectionState: 'live',
        loadError: ''
      }
    case 'unauthorized':
      return { ...state, connectionState: 'error', loadError: '' }
    case 'refresh-error':
      return {
        ...state,
        connectionState: state.runs.length === 0 ? 'error' : 'degraded',
        loadError: action.message
      }
    case 'load-more-success':
      return {
        ...state,
        // Keep the already loaded prefix in place and append the next page.
        runs: mergeRunListsWithoutRegression(action.runs, state.runs),
        nextOffset:
          action.runs.length === action.pagination.limit
            ? action.pagination.offset + action.pagination.limit
            : undefined
      }
    case 'load-more-error':
      return { ...state, connectionState: 'degraded', loadError: action.message }
    case 'connection':
      return { ...state, connectionState: action.value }
  }
}

function initialDashboardData(hasLiveApi: boolean): DashboardDataState {
  return {
    runs: hasLiveApi ? [] : fixtureRuns,
    aggregates: {},
    ...(hasLiveApi ? {} : { operations: fixtureOperations }),
    connectionState: hasLiveApi ? 'loading' : 'fixture',
    loadError: '',
    nextOffset: hasLiveApi ? 0 : undefined
  }
}

/** Route segments arrive percent-encoded; a malformed escape falls through. */
function decodeRouteKey(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

function aggregateKindForRoute(section: Section): AggregateKind | undefined {
  if (section === 'pull-requests') return 'pull-request'
  if (section === 'suites') return 'suite'
  if (section === 'scenarios') return 'scenario'
  return undefined
}

function includeRequestedAggregate(
  aggregates: RunAggregateProjectionV2[],
  requested: RunAggregateProjectionV2 | undefined
): RunAggregateProjectionV2[] {
  if (!requested || aggregates.some((aggregate) => aggregate.key === requested.key)) {
    return aggregates
  }
  return [requested, ...aggregates]
}

/** `#/runs/:id/review` — the candidate-review subview under a run. */
function parseSubview(hash: string): 'review' | undefined {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean)
  return parts[0] === 'runs' && parts[2] === 'review' ? 'review' : undefined
}

function currentDashboardRoute(): { section: Section; runId?: string; subview?: 'review' } {
  return {
    ...parseDashboardRoute(window.location.hash),
    ...(parseSubview(window.location.hash) ? { subview: 'review' as const } : {})
  }
}

const ROUTE_SCROLL_PREFIX = 'wtd-route-scroll:'

function routeScrollKey(hash: string): string {
  return `${ROUTE_SCROLL_PREFIX}${hash || '#/runs'}`
}

function saveRouteScroll(hash: string): void {
  try {
    window.sessionStorage.setItem(routeScrollKey(hash), String(window.scrollY))
  } catch {
    // Scroll restoration remains best-effort in constrained embedded browsers.
  }
}

function restoreRouteScroll(hash: string): void {
  let top = 0
  try {
    const stored = window.sessionStorage.getItem(routeScrollKey(hash))
    if (stored !== null) {
      const parsed = Number(stored)
      if (Number.isFinite(parsed) && parsed >= 0) top = parsed
    }
  } catch {
    // Defaulting to the top is safe when session storage is unavailable.
  }
  const restore = () => window.scrollTo({ top, left: 0, behavior: 'auto' })
  if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(restore)
  else window.setTimeout(restore, 0)
}

function useRoute(): { section: Section; runId?: string; subview?: 'review' } {
  const [route, setRoute] = useState(currentDashboardRoute)
  const previousHash = useRef(window.location.hash)
  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    restoreRouteScroll(previousHash.current)
    const onHashChange = () => {
      saveRouteScroll(previousHash.current)
      previousHash.current = window.location.hash
      setRoute(currentDashboardRoute())
      restoreRouteScroll(previousHash.current)
    }
    window.addEventListener('hashchange', onHashChange)
    return () => {
      saveRouteScroll(previousHash.current)
      window.history.scrollRestoration = previousRestoration
      window.removeEventListener('hashchange', onHashChange)
    }
  }, [])
  return route
}

/**
 * Run detail with its live feed. Fixture mode serves committed data with a
 * clearly-labelled simulation; with a verifier configured it polls the
 * resumable event endpoint. Hooks live here so the router stays hook-free.
 */
function RunDetailRoute({ runId, now }: { runId: string; now: number }) {
  const feed = useRunFeed(runId)
  return <RunDetailPage run={feed.run} events={feed.events} now={now} feed={feed} />
}

function CandidateReviewRoute({ runId }: { runId: string }) {
  const feed = useRunFeed(runId)
  const review = useCandidateReview(runId)
  return <CandidateReviewPage runId={runId} run={feed.run} review={review} />
}

export function App() {
  const route = useRoute()
  const now = Date.now()
  const [menuOpen, setMenuOpen] = useState(false)
  const apiBaseUrl = import.meta.env.VITE_WEB_TEST_API_BASE_URL
  const hasLiveApi = typeof apiBaseUrl === 'string' && apiBaseUrl.length > 0
  const [dashboardData, dispatchDashboardData] = useReducer(
    dashboardDataReducer,
    hasLiveApi,
    initialDashboardData
  )
  const [refreshToken, setRefreshToken] = useState(0)
  const [accessState, setAccessState] = useState<DashboardAccessState | undefined>()
  const stackedParent = route.runId
    ? route.section === 'runs' && route.subview === 'review'
      ? { href: `#/runs/${route.runId}`, label: 'Back to run' }
      : {
          href: `#/${route.section}`,
          label:
            route.section === 'pull-requests'
              ? 'Pull requests'
              : route.section === 'suites'
                ? 'Suites'
                : route.section === 'scenarios'
                  ? 'Scenarios'
                  : 'Runs'
        }
    : undefined
  const waitingForInitialWorkspace =
    hasLiveApi && dashboardData.runs.length === 0 && dashboardData.connectionState !== 'live'
  const [loadingMore, setLoadingMore] = useState(false)
  const requestedAggregateKind = route.runId ? aggregateKindForRoute(route.section) : undefined
  const requestedAggregateKey = route.runId ? decodeRouteKey(route.runId) : undefined

  useEffect(() => {
    if (!hasLiveApi) return
    let stopped = false
    let refreshInFlight = false
    let activeController: AbortController | undefined
    const refresh = async (): Promise<void> => {
      if (stopped || refreshInFlight) return
      refreshInFlight = true
      const controller = new AbortController()
      activeController = controller
      const timeout = window.setTimeout(() => controller.abort(), workspaceRefreshTimeoutMs)
      const api = createDashboardApi(apiBaseUrl, (input, init) =>
        fetch(input, { ...init, signal: controller.signal })
      )
      try {
        const [next, pullRequests, suites, scenarios, operations, requestedAggregate] =
          await Promise.all([
            api.listRunsPage({ limit: 100 }),
            api.listAggregates('pull-request'),
            api.listAggregates('suite'),
            api.listAggregates('scenario'),
            api.getOperations(),
            requestedAggregateKind && requestedAggregateKey
              ? api.getAggregate(requestedAggregateKind, requestedAggregateKey)
              : Promise.resolve(undefined)
          ])
        if (stopped) return
        setAccessState(undefined)
        dispatchDashboardData({
          type: 'refresh-success',
          runs: next.runs,
          pagination: next.pagination,
          pullRequests: includeRequestedAggregate(
            pullRequests,
            requestedAggregate?.kind === 'pull-request' ? requestedAggregate : undefined
          ),
          suites: includeRequestedAggregate(
            suites,
            requestedAggregate?.kind === 'suite' ? requestedAggregate : undefined
          ),
          scenarios: includeRequestedAggregate(
            scenarios,
            requestedAggregate?.kind === 'scenario' ? requestedAggregate : undefined
          ),
          operations
        })
      } catch (error) {
        const deadlineAborted = controller.signal.aborted
        // Promise.all rejects on the first failed endpoint. Cancel every
        // sibling before releasing the in-flight guard so a hanging request
        // cannot survive its batch and overlap the next interval tick.
        controller.abort()
        if (stopped) return
        if (error instanceof DashboardApiError && [401, 403].includes(error.status)) {
          setAccessState(error.status === 403 ? 'non-admin' : 'signed-out')
          dispatchDashboardData({ type: 'unauthorized' })
          return
        }
        if (
          error instanceof DashboardApiError &&
          error.status === 503 &&
          ['auth_service_unavailable', 'auth_misconfigured'].includes(error.code)
        ) {
          setAccessState('unavailable')
          dispatchDashboardData({ type: 'unauthorized' })
          return
        }
        dispatchDashboardData({
          type: 'refresh-error',
          message: deadlineAborted
            ? 'The verifier refresh timed out.'
            : error instanceof Error
              ? error.message
              : 'The verifier could not be reached.'
        })
      } finally {
        window.clearTimeout(timeout)
        if (activeController === controller) activeController = undefined
        refreshInFlight = false
      }
    }
    void refresh()
    const interval = window.setInterval(() => void refresh(), workspaceRefreshIntervalMs)
    return () => {
      stopped = true
      activeController?.abort()
      window.clearInterval(interval)
    }
  }, [apiBaseUrl, hasLiveApi, refreshToken, requestedAggregateKey, requestedAggregateKind])

  async function loadMoreRuns(): Promise<void> {
    if (!hasLiveApi || dashboardData.nextOffset === undefined) return
    setLoadingMore(true)
    try {
      const page = await createDashboardApi(apiBaseUrl).listRunsPage({
        limit: 100,
        offset: dashboardData.nextOffset
      })
      dispatchDashboardData({
        type: 'load-more-success',
        runs: page.runs,
        pagination: page.pagination
      })
    } catch (error) {
      dispatchDashboardData({
        type: 'load-more-error',
        message: error instanceof Error ? error.message : 'More runs could not be loaded.'
      })
    } finally {
      setLoadingMore(false)
    }
  }

  // Belt over the per-link close: any route change dismisses the menu.
  useEffect(() => setMenuOpen(false), [route.section, route.runId])

  if (hasLiveApi && accessState) {
    return (
      <DashboardAccess
        state={accessState}
        signInUrl={buildDashboardSignInUrl(apiBaseUrl, window.location.href)}
        onRetry={() => {
          setAccessState(undefined)
          dispatchDashboardData({ type: 'connection', value: 'loading' })
          setRefreshToken((value) => value + 1)
        }}
      />
    )
  }

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        onClick={(event) => {
          event.preventDefault()
          document.getElementById('main')?.focus()
        }}
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:ring-[3px] focus:ring-ring/50"
      >
        Skip to content
      </a>
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-[1120px] items-center gap-6 px-6 max-sm:gap-4 max-sm:px-4">
          <a
            href="#/runs"
            aria-label="Blume Web tests — Runs"
            className="-ml-1.5 flex size-9 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            <img
              src={blumeFlowerUrl}
              alt=""
              aria-hidden
              draggable={false}
              className="size-6 select-none"
            />
          </a>
          <nav aria-label="Sections" className="min-w-0 flex-1 self-stretch max-sm:hidden">
            <ul className="flex h-full items-stretch gap-1">
              {sections.map((section) => {
                const current = route.section === section.id
                return (
                  <li key={section.id} className="flex items-stretch">
                    <a
                      href={`#/${section.id}`}
                      aria-current={current ? 'page' : undefined}
                      className={cn(
                        'relative flex items-center px-3 text-[13px] font-medium whitespace-nowrap transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                        current
                          ? 'text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {section.label}
                    </a>
                  </li>
                )
              })}
            </ul>
          </nav>
          <div className="flex shrink-0 items-center gap-1 max-sm:ml-auto">
            {dashboardData.connectionState === 'fixture' ? <DemoBadge /> : null}
            <ThemeToggle />
            {/* Below sm the inline nav is hidden entirely — never clipped or
             * blind-scrolled — and every section moves into this sheet. */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="sm:hidden">
                  <Menu aria-hidden />
                  Menu
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                showCloseButton={false}
                aria-describedby={undefined}
                className="gap-0"
              >
                <SheetHeader className="flex-row items-center justify-between border-b py-2.5 pr-2.5">
                  <SheetTitle className="text-sm font-semibold tracking-tight">Sections</SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon">
                      <X aria-hidden />
                      <span className="sr-only">Close menu</span>
                    </Button>
                  </SheetClose>
                </SheetHeader>
                <nav aria-label="Sections menu" className="p-2">
                  <ul className="space-y-0.5">
                    {sections.map((section) => {
                      const current = route.section === section.id
                      return (
                        <li key={section.id}>
                          <a
                            href={`#/${section.id}`}
                            aria-current={current ? 'page' : undefined}
                            onClick={() => setMenuOpen(false)}
                            className={cn(
                              'flex h-11 items-center rounded-md px-3 text-sm transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                              current
                                ? 'bg-secondary font-semibold text-foreground'
                                : 'font-medium text-muted-foreground hover:bg-accent hover:text-foreground'
                            )}
                          >
                            {section.label}
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main
        id="main"
        tabIndex={-1}
        className="mx-auto max-w-[1120px] px-6 py-10 focus:outline-none max-sm:px-4 max-sm:py-8"
      >
        <ConnectionNotice
          state={dashboardData.connectionState}
          error={dashboardData.loadError}
          hasData={dashboardData.runs.length > 0}
          compact={stackedParent !== undefined}
          onRetry={() => {
            dispatchDashboardData({ type: 'connection', value: 'loading' })
            setRefreshToken((value) => value + 1)
          }}
        />
        {waitingForInitialWorkspace ? (
          stackedParent ? (
            <BackLink href={stackedParent.href} label={stackedParent.label} />
          ) : null
        ) : route.section === 'runs' && route.runId && route.subview === 'review' ? (
          <CandidateReviewRoute runId={route.runId} />
        ) : route.section === 'runs' && route.runId ? (
          <RunDetailRoute runId={route.runId} now={now} />
        ) : route.section === 'runs' ? (
          <RunsPage
            runs={dashboardData.runs}
            now={now}
            hasMore={dashboardData.nextOffset !== undefined}
            loadingMore={loadingMore}
            onLoadMore={loadMoreRuns}
          />
        ) : route.section === 'pull-requests' && route.runId ? (
          <PullRequestDetailPage
            runs={dashboardData.runs}
            aggregates={dashboardData.aggregates['pull-request']}
            groupKey={decodeRouteKey(route.runId)}
            now={now}
          />
        ) : route.section === 'pull-requests' ? (
          <PullRequestsPage
            runs={dashboardData.runs}
            aggregates={dashboardData.aggregates['pull-request']}
            now={now}
          />
        ) : route.section === 'suites' && route.runId ? (
          <SuiteDetailPage
            runs={dashboardData.runs}
            aggregates={dashboardData.aggregates.suite}
            groupKey={decodeRouteKey(route.runId)}
            now={now}
          />
        ) : route.section === 'suites' ? (
          <SuitesPage
            runs={dashboardData.runs}
            aggregates={dashboardData.aggregates.suite}
            now={now}
          />
        ) : route.section === 'scenarios' && route.runId ? (
          <ScenarioDetailPage
            runs={dashboardData.runs}
            aggregates={dashboardData.aggregates.scenario}
            scenarioId={decodeRouteKey(route.runId)}
            now={now}
          />
        ) : route.section === 'scenarios' ? (
          <ScenariosPage
            runs={dashboardData.runs}
            aggregates={dashboardData.aggregates.scenario}
            now={now}
          />
        ) : dashboardData.operations ? (
          <OperationsPage snapshot={dashboardData.operations} />
        ) : null}
      </main>
    </div>
  )
}

function ConnectionNotice({
  state,
  error,
  hasData,
  compact,
  onRetry
}: {
  state: ConnectionState
  error: string
  hasData: boolean
  compact?: boolean
  onRetry(): void
}) {
  if (state === 'live') return null
  if (state === 'fixture') return null
  if (state === 'loading') {
    return (
      <p
        role="status"
        className={cn(
          'flex items-center gap-2 text-sm text-muted-foreground',
          hasData || compact ? 'mb-4' : 'justify-center py-16'
        )}
      >
        <LoaderCircle aria-hidden className="size-4 animate-spin" />
        {hasData ? 'Refreshing verifier data…' : 'Loading verifier data…'}
      </p>
    )
  }
  return (
    <div
      role={state === 'error' ? 'alert' : 'status'}
      className={cn(
        'mb-4 flex max-w-3xl flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-muted px-4 py-3 text-sm',
        !hasData && !compact && 'mx-auto mt-12'
      )}
    >
      <TriangleAlert aria-hidden className="size-4 shrink-0 text-status-warn" />
      <p className="min-w-0 flex-1">
        {state === 'degraded'
          ? 'Updates are paused. The last successfully loaded verifier data remains visible.'
          : 'The verifier workspace could not be loaded.'}{' '}
        {error ? <span className="text-muted-foreground">{error}</span> : null}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

function DemoBadge() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            tabIndex={0}
            data-demo-badge
            className="h-6 cursor-default border-status-warn/30 bg-status-warn-surface px-2 text-[11px] font-semibold text-status-warn focus-visible:outline-none"
          >
            Demo
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={8}>
          Connect a verifier build to inspect real team runs.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function DashboardAccess({
  state,
  signInUrl,
  onRetry
}: {
  state: DashboardAccessState
  signInUrl: string
  onRetry(): void
}) {
  const copy =
    state === 'non-admin'
      ? {
          title: 'Administrator access required',
          description:
            'Your Blume account is signed in, but it does not have the administrator role required for this developer workspace.',
          action: 'Use another Blume account'
        }
      : state === 'unavailable'
        ? {
            title: 'Sign-in service unavailable',
            description:
              'Blume could not verify your identity or administrator role. No dashboard data was exposed. Try again in a moment.',
            action: 'Try again'
          }
        : {
            title: 'Sign in to Blume web tests',
            description:
              'Private Blume developer workspace. Continue with your existing Blume administrator account.',
            action: 'Continue with Blume'
          }
  return (
    <main className="grid min-h-dvh place-items-center px-4 py-12">
      <Card className="w-full max-w-sm gap-0 rounded-lg py-0">
        <CardHeader className="gap-3 px-6 pt-6 pb-4">
          <ShieldCheck aria-hidden className="size-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-xl">{copy.title}</CardTitle>
            <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{copy.description}</p>
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6">
          {state === 'unavailable' ? (
            <Button className="w-full" type="button" onClick={onRetry}>
              {copy.action}
            </Button>
          ) : (
            <Button className="w-full" asChild>
              <a href={signInUrl}>{copy.action}</a>
            </Button>
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            For Blume team development only · Administrator access
          </p>
        </CardContent>
      </Card>
    </main>
  )
}
